import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet"; // ← Add this: npm install react-helmet
import "../Login/Login.css";
import dlogin from "../Images/dlogin.png";

const Login = ({ onLogin }) => {
  const [userType, setUserType] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verifyId, setVerifyId] = useState("");
  const [otpTimer, setOtpTimer] = useState(300);
  const [canResend, setCanResend] = useState(true);
  const navigate = useNavigate();

  const otpInputRef = useRef(null);

  // OTP Timer
  useEffect(() => {
    let timer;
    if (isOtpSent && otpTimer > 0) {
      timer = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else if (otpTimer <= 0 && isOtpSent) {
      setError("OTP expired. Please resend a new OTP.");
      setIsOtpSent(false);
      setVerifyId("");
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [isOtpSent, otpTimer]);

  // Auto-focus OTP input when sent
  useEffect(() => {
    if (isOtpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [isOtpSent]);

  // Reset form
  const resetForm = () => {
    setEmail("");
    setPassword("");
    setPhone("");
    setOtp("");
    setIsOtpSent(false);
    setError("");
    setSuccessMessage("");
    setVerifyId("");
    setOtpTimer(300);
    setCanResend(true);
  };

  // Hard refresh to bypass cache
  const handleHardRefresh = () => {
    window.location.reload(true); // true = force reload from server (bypasses cache)
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (!canResend) return;

    setError("");
    setSuccessMessage("");
    setOtp("");
    setVerifyId("");
    setOtpTimer(300);
    setCanResend(false);

    if (!/^\d{10}$/.test(phone)) {
      setCanResend(true);
      return setError("Please enter a valid 10-digit Indian phone number.");
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        "https://aadyaacademy.org/api/send-otp-teacher",
        { phoneNumber: phone },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        setIsOtpSent(true);
        setSuccessMessage(`OTP sent to ${phone}. Check your messages.`);
        setVerifyId(response.data.verify_id);
        setTimeout(() => setCanResend(true), 30000); // 30s cooldown
      } else {
        setError(response.data.message || "Failed to send OTP. Try again.");
        setCanResend(true);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Error sending OTP. Please try in incognito mode or desktop site.";
      setError(msg);
      setCanResend(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Submit login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      if (userType === "admin" || userType === "chairman") {
        if (!email.trim() || !password.trim()) {
          setError("Email and password are required.");
          setIsLoading(false);
          return;
        }

        const response = await axios.post(
          "https://aadyaacademy.org/api/login",
          { email, password },
          { withCredentials: true, headers: { "Content-Type": "application/json" } }
        );

        if (response.data.success) {
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userRole", userType);
          localStorage.setItem("userData", JSON.stringify(response.data.user));
          onLogin(userType);
          window.location.replace("/teacher/dashboard");
        } else {
          setError(response.data.message || "Invalid email or password.");
        }
      } else {
        // Teacher OTP login
        if (!phone.trim() || !otp.trim()) {
          setError("Phone number and OTP are required.");
          setIsLoading(false);
          return;
        }
        if (!/^\d{6}$/.test(otp)) {
          setError("OTP must be 6 digits.");
          setIsLoading(false);
          return;
        }
        if (!verifyId) {
          setError("Please request a new OTP.");
          setIsLoading(false);
          return;
        }

        const response = await axios.post(
          "https://aadyaacademy.org/api/verify-otp-teacher",
          { phoneNumber: phone, otp, verify_id: verifyId },
          { withCredentials: true, headers: { "Content-Type": "application/json" } }
        );

        if (response.data.message?.toLowerCase().includes("otp verified")) {
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userRole", "teacher");
          localStorage.setItem("userPhone", phone);
          localStorage.setItem("userData", JSON.stringify(response.data.teacher || {}));
          onLogin("teacher");
          window.location.replace("/teacher/dashboard");
        } else {
          setError(response.data.message || "Invalid OTP.");
        }
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Login failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimer = () => {
    const minutes = Math.floor(otpTimer / 60);
    const seconds = otpTimer % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="login-c">
      <Helmet>
        <meta httpEquiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <meta httpEquiv="Expires" content="0" />
      </Helmet>

      <div className="login-container">
        <div className="login-left">
          <div className="login-left-c">
            <img src={dlogin} alt="Login illustration" />
          </div>
        </div>

        <div className="login-right">
          <div className="login-right-c">
            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <label>User Type</label>
                <select
                  value={userType}
                  onChange={(e) => {
                    setUserType(e.target.value);
                    resetForm();
                  }}
                >
                  <option value="admin">Admin</option>
                  <option value="chairman">Chairman</option>
                  <option value="teacher">Teacher</option>
                </select>
              </div>

              {(userType === "admin" || userType === "chairman") ? (
                <>
                  <div className="input-group">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="input-group">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="input-group">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} // only digits
                      placeholder="10-digit Phone Number"
                      maxLength={10}
                      required
                    />
                  </div>

                  {!isOtpSent ? (
                    <div className="input-group">
                      <button
                        type="button"
                        className="sub"
                        onClick={handleSendOtp}
                        disabled={isLoading || !canResend}
                      >
                        {isLoading ? "Sending..." : "Send OTP"}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="input-group">
                        <input
                          ref={otpInputRef}
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                          placeholder="Enter 6-digit OTP"
                          maxLength={6}
                          required
                        />
                      </div>

                      <div className="input-group" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                        <button
                          type="button"
                          className="sub"
                          onClick={handleSendOtp}
                          disabled={isLoading || !canResend}
                        >
                          {isLoading ? "Sending..." : "Resend OTP"}
                        </button>

                        {isOtpSent && otpTimer > 0 && (
                          <small style={{ marginTop: "8px", color: "#555" }}>
                            OTP expires in: {formatTimer()}
                          </small>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {error && <div className="error-message" style={{ color: "red", margin: "10px 0" }}>{error}</div>}
              {successMessage && <div className="success-message" style={{ color: "green", margin: "10px 0" }}>{successMessage}</div>}

              {/* Helpful note for cache/OTP issues */}
              <small style={{ color: "#666", display: "block", margin: "10px 0" }}>
                OTP not sending? Try: 
                <strong> Incognito mode</strong>, <strong>Desktop site</strong> (in browser menu), or{" "}
                <button
                  type="button"
                  onClick={handleHardRefresh}
                  style={{ background: "none", border: "none", color: "blue", cursor: "pointer", padding: 0 }}
                >
                  Refresh page
                </button>.
              </small>

              <div className="action-row">
                {(userType === "admin" || userType === "chairman" || isOtpSent) && (
                  <button className="sub" type="submit" disabled={isLoading}>
                    {isLoading
                      ? userType === "teacher"
                        ? "Verifying OTP..."
                        : "Logging in..."
                      : userType === "teacher"
                      ? "Verify OTP & Login"
                      : "Login"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;